import React from 'react';

/**
 * One heading treatment for every public-portal section.
 *
 * The sections had grown five different headers between them — a 1×5 accent
 * bar with a 16px title here, a 1.5×7 bar with an 18px title there, some with
 * an eyebrow, some with a subtitle indented by a hardcoded 14px. Scrolling the
 * page, nothing lined up. This is the single treatment they all now use:
 *
 *   EYEBROW (accent, tracked out)
 *   Title, large and tight
 *   Optional one-line subtitle
 *                                              [ optional action, right ]
 *
 * Everything themes off --portal-accent, so a UDA page renders it in that
 * authority's colour with no per-section changes.
 */
interface Props {
  /** Small tracked-out label above the title. */
  eyebrow?: string;
  /** The section title. */
  title: string;
  /** Optional supporting line beneath the title. */
  subtitle?: string;
  /** Optional right-aligned slot — a "View all" link, a count badge, etc. */
  action?: React.ReactNode;
  /** Centre the block (used by full-width sections that have no action). */
  align?: 'left' | 'center';
  className?: string;
}

export const SectionHeading: React.FC<Props> = ({
  eyebrow,
  title,
  subtitle,
  action,
  align = 'left',
  className = '',
}) => {
  const centered = align === 'center';

  return (
    <div
      className={`flex flex-col gap-4 mb-8 sm:mb-10 ${
        centered ? 'items-center text-center' : 'sm:flex-row sm:items-end sm:justify-between'
      } ${className}`}
    >
      <div className={centered ? 'max-w-2xl' : 'min-w-0'}>
        {eyebrow && (
          <div className={`flex items-center gap-2.5 mb-2.5 ${centered ? 'justify-center' : ''}`}>
            <span className="w-6 h-[3px] rounded-full bg-[var(--portal-accent)]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[color:var(--portal-accent-dark)] dark:text-[color:var(--portal-accent-light)]">
              {eyebrow}
            </span>
            {centered && <span className="w-6 h-[3px] rounded-full bg-[var(--portal-accent)]" />}
          </div>
        )}

        <h2 className="text-[22px] sm:text-[26px] lg:text-[30px] font-extrabold leading-[1.15] tracking-[-0.02em] text-[#161A22] dark:text-white">
          {title}
        </h2>

        {subtitle && (
          <p className="mt-2.5 text-[14px] sm:text-[15px] leading-relaxed text-[#5B6472] dark:text-white/60 max-w-2xl">
            {subtitle}
          </p>
        )}
      </div>

      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
};
